import { ele } from "cables-shared-client";
import web from "./index.js";

export default class Tabs
{
    init()
    {
        const tabs = ele.byQueryAll("ul.tab-bar li");
        this.initTabs(tabs);
        this._activeTab = null;
    }

    initTabs(tabs)
    {
        let hasActiveTab = false;
        tabs.forEach((tab) =>
        {
            if (tab.dataset.tabName)
            {
                ele.clickable(tab, () =>
                {
                    this.activateTab(tabs, tab, !tab.classList.contains("nested"));
                });

                if (window.location.hash && (window.location.hash === "#" + tab.dataset.tabName))
                {
                    this.activateTab(tabs, tab);
                    hasActiveTab = true;
                    if (!this._elementIsVisibleInViewport(tab))
                    {
                        setTimeout(() =>
                        {
                            tab.scrollIntoView();
                        }, 500);
                    }
                }
                if (tab.dataset.getCount)
                {
                    web.executeFunctionByName(tab.dataset.getCount, window, tab, false);
                }
            }
            if (tab.classList.contains("active"))
            {
                hasActiveTab = true;
            }
        });

        if (!hasActiveTab)
        {
            let defaultTab = null;
            for (let i = 0; i < tabs.length; i++)
            {
                const tab = tabs[i];
                if (tab.dataset.tabName && tab.classList.contains("active"))
                {
                    defaultTab = tab;
                    break;
                }
            }
            if (!defaultTab) defaultTab = tabs[0];
            this.activateTab(tabs, defaultTab);
        }

        window.addEventListener("hashchange", (event) =>
        {
            if (event.newURL)
            {
                try
                {
                    const url = new URL(event.newURL);
                    tabs.forEach((tab) =>
                    {
                        if (url.hash && (url.hash === "#" + tab.dataset.tabName))
                        {
                            this.activateTab(tabs, tab);
                        }
                    });
                }
                catch (e)
                {
                    // ignore broken urls
                }
            }
        }, false);
    }

    activateTab(tabs, tabToActivate, fromUserInteraction = false)
    {
        if (!tabToActivate) return;
        if (tabToActivate === this._activeTab) return;

        tabs.forEach((tab) =>
        {
            if (tab.dataset.tabName)
            {
                tab.classList.remove("active");
                const selector = ".tab-bar-content[data-tab-name=\"" + tab.dataset.tabName + "\"]";
                const oldContent = ele.byQuery(selector);
                if (oldContent) ele.hide(oldContent);
            }
        });
        tabToActivate.classList.add("active");
        if (tabToActivate.dataset.tabName)
        {
            if (fromUserInteraction)
            {
                if (history.pushState)
                {
                    let url = new URL(window.location);
                    url.searchParams.delete("l");
                    url.searchParams.delete("o");
                    url.hash = "#" + tabToActivate.dataset.tabName;
                    window.history.pushState({}, "", url);
                }
                else
                {
                    location.hash = "#" + tabToActivate.dataset.tabName;
                }
            }

            const selector = ".tab-bar-content[data-tab-name=\"" + tabToActivate.dataset.tabName + "\"]";
            const contentEle = ele.byQuery(selector);
            if (contentEle)
            {
                const loadingStatus = "<br/><br/><br/><div class=\"loading\" style=\"margin-bottom:600px;\"></div>";
                if (contentEle.dataset.getContent)
                {
                    if (contentEle.dataset.targetElement)
                    {
                        const targetElement = document.querySelector(contentEle.dataset.targetElement);
                        if (targetElement)
                        {
                            targetElement.innerHTML = loadingStatus;
                            web.executeFunctionByName(contentEle.dataset.getContent, window, targetElement, fromUserInteraction);
                        }
                        else
                        {
                            contentEle.innerHTML = loadingStatus;
                            web.executeFunctionByName(contentEle.dataset.getContent, window, contentEle, fromUserInteraction);
                        }
                    }
                    else
                    {
                        contentEle.innerHTML = loadingStatus;
                        web.executeFunctionByName(contentEle.dataset.getContent, window, contentEle, fromUserInteraction);
                    }
                }
                ele.show(contentEle);
            }
        }
        this._activeTab = tabToActivate;
        tabToActivate.dispatchEvent(new CustomEvent("activated", { "detail": { "fromUserInteraction": fromUserInteraction } }));
    }

    requestedTabExists(tabs)
    {
        if (!tabs) return false;
        if (!window.location.hash) return false;
        return Array.from(tabs).some((tab) => { return tab.dataset.tabName && "#" + tab.dataset.tabName === window.location.hash; });
    }

    isRequestedTab(tabs, tabElement)
    {
        if (!tabs || !tabElement) return false;
        if (!window.location.hash) return false;
        return Array.from(tabs).some((tab) => { return tab.dataset.tabName && "#" + tab.dataset.tabName === window.location.hash && tab.dataset.tabName === tabElement.dataset.tabName; });
    }

    initPagination(containerElement, pageInfo, limit, offset, callback)
    {
        limit = Number(limit);
        offset = Number(offset);

        if (!callback)
        {
            callback = (newLimit, newOffset) =>
            {
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set("l", newLimit);
                searchParams.set("o", newOffset);
                window.location.search = searchParams.toString();
            };
        }

        const url = new URL(window.location.href);
        if (!limit)
        {
            limit = Number(limit);
            limit = url.searchParams.get("l") || 48;
        }

        if (!offset)
        {
            offset = url.searchParams.get("o");
            offset = Number(offset);
        }

        if (containerElement)
        {
            const oldPagination = containerElement.querySelector(".row.pagination");
            if (oldPagination) oldPagination.remove();
            containerElement.innerHTML += web.getHandleBarHtml("pagination", pageInfo);

            const selectorBar = containerElement.querySelector(".pagination .tab-bar");
            if (selectorBar)
            {
                const prevButtons = selectorBar.querySelectorAll(".prev");
                prevButtons.forEach((prevButton) =>
                {
                    if (prevButton)
                    {
                        const prev = () =>
                        {
                            const newOffset = Math.max(0, offset - limit);
                            callback(limit, newOffset);
                        };

                        prevButton.removeEventListener("click", prev);
                        prevButton.addEventListener("click", prev);
                    }
                });

                const nextButtons = selectorBar.querySelectorAll(".next");
                nextButtons.forEach((nextButton) =>
                {
                    if (nextButton)
                    {
                        const next = () =>
                        {
                            const newOffset = Math.min(pageInfo.count, offset + limit);
                            callback(limit, newOffset);
                        };
                        nextButton.removeEventListener("click", next);
                        nextButton.addEventListener("click", next);
                    }
                });

                const moreButtons = selectorBar.querySelectorAll(".more");
                moreButtons.forEach((moreButton) =>
                {
                    if (moreButton)
                    {
                        const more = () =>
                        {
                            callback(0, 0);
                        };
                        moreButton.removeEventListener("click", more);
                        moreButton.addEventListener("click", more);
                    }
                });

                const paginationTabs = selectorBar.querySelectorAll("li.page");
                paginationTabs.forEach((tab, i) =>
                {
                    tab.addEventListener("click", (e) =>
                    {
                        let pageIndex = i;
                        if (tab.dataset.pageIndex)
                        {
                            pageIndex = Number(tab.dataset.pageIndex) - 1;
                        }
                        const newOffset = pageIndex * limit;
                        callback(limit, newOffset);
                    });
                });
            }
        }
    }

    addPagination(apiUrl, limit = 48, offset = 0)
    {
        let url = new URL(window.location.href);
        limit = Number(url.searchParams.get("l")) || limit;
        offset = Number(url.searchParams.get("o")) || offset;

        const pagedUrl = new URL(apiUrl, "https://cables.gl/"); // stephan: baseurl placeholder to manipulate getstring in next line, value is ignored
        if (offset)
        {
            pagedUrl.searchParams.set("o", String(offset));
        }
        if (limit || limit === 0)
        {
            pagedUrl.searchParams.set("l", String(limit));
        }

        return pagedUrl.pathname + pagedUrl.search;
    }

    _elementIsVisibleInViewport(el, partiallyVisible = false)
    {
        const { top, left, bottom, right } = el.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;
        return partiallyVisible
            ? ((top > 0 && top < innerHeight) ||
                (bottom > 0 && bottom < innerHeight)) &&
            ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
            : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
    }
}
